import type { FC } from 'react';
import React, { useState, useCallback, useRef, forwardRef } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { styled, css } from '@compiled/react';
import { mergeRefs } from 'use-callback-ref';

import Textfield from '@atlaskit/textfield/text-field';
import SearchIcon from '@atlaskit/icon/core/search';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import SelectClearIcon from '@atlaskit/icon/core/cross-circle';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import TooltipPrimitive from '@atlaskit/tooltip/TooltipPrimitive';
import type { TooltipPrimitiveProps } from '@atlaskit/tooltip/TooltipPrimitive';

import type { HoverPageCardProps } from '@confluence/page-card/entry-points/useHoverPageCard.ts';
import { useSSRPlaceholderReplaceIdProp, LoadableAfterPaint } from '@confluence/loadable';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import { TOUCH, START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { enrichExcerpt } from '@confluence/search-utils/entry-points/excerpt.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useDebounce } from '@confluence/debounce';
import { useIsCreationDropdownEnabled } from '@confluence/content-types-utils/entry-points/useIsCreationDropdownEnabled.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import { useContentTreeSelectionActions } from '@confluence/content-tree-actions/entry-points/store.ts';
import { useContentTreeSearch } from '@confluence/content-tree-search/entry-points/useContentTreeSearch.ts';
import {
	ContentTreeSearchEmptyLoader,
	ContentTreeSearchErrorLoader,
} from '@confluence/content-tree-search/entry-points/ContentTreeSearchStates.ts';

import { SpaceViewsList } from './SpaceViewsList/SpaceViewsList.tsx';
import type { SpaceViewsPersistenceOption } from './__generated__/useSpaceViewsOptionsPersistenceQuery.graphql.ts';

const i18n = defineMessages({
	searchLabel: {
		id: 'space-views.content-tree-search.placeholder',
		defaultMessage: 'Search by title',
		description: 'Placeholder text for content tree search input',
	},
	clearSearch: {
		id: 'space-views.content-tree-search.clear-search',
		defaultMessage: 'Clear search',
		description:
			'The text is shown as a tooltip on the clear button in the content tree search input, which appears after the user has entered a search query and allows them to clear the current search and reset the content tree view.',
	},
	searchInputDescription: {
		id: 'space-views.content-tree-search.input-description',
		defaultMessage: 'Results will update as you type.',
		description: 'Aria description for content tree search input',
	},
	searchResultsCount: {
		id: 'space-views.content-tree-search.results-count',
		defaultMessage: '{count} {count, plural, one {result} other {results}} found',
		description: 'Screen reader only text with search results count in content tree search',
	},
	disabledSearchTooltip: {
		id: 'space-views.content-tree-search.disabled-label',
		defaultMessage: 'Search is disabled when editing multiple items',
		description: 'Tooltip for content tree search when it is disabled due to the multi-select mode',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextfieldWrapperDiv = styled.div({
	marginTop: token('space.100'),
	marginBottom: token('space.100'),
	marginLeft: token('space.negative.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-ds--text-field--container]': {
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ClearSearchButton = styled.button({
	border: 'none',
	backgroundColor: 'transparent',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginRight: '1px',
	height: '26px',
	width: '26px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingRight: '9px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	cursor: 'pointer',
});

// same styles as a tooltip but with narrower width
const tooltipStyles = css({
	background: `${token('color.background.neutral.bold')}`,
	color: `${token('color.text.inverse')}`,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	font: token('font.body.small'),
	padding: '2px 6px',
	maxWidth: '188px',
});

// same styles as a tooltip but with narrower width
const tooltipStylesUpdated = css({
	background: `${token('color.background.neutral.bold')}`,
	color: `${token('color.text.inverse')}`,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small'),
	font: token('font.body.small'),
	padding: '2px 6px',
	maxWidth: '188px',
});

const ToolTipOverride = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function ToolTipOverride(
	{ children, ...props },
	ref,
) {
	return (
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
		<TooltipPrimitive
			{...props}
			ref={ref}
			css={[tooltipStyles, fg('confluence_frontend_tokenize_border') && tooltipStylesUpdated]}
		>
			{children}
		</TooltipPrimitive>
	);
}) as React.ForwardRefExoticComponent<TooltipPrimitiveProps>;

type ContentTreeSearchProps = React.PropsWithChildren<
	Partial<HoverPageCardProps> & {
		contentTreeSize?: number;
		hideSearchRow?: boolean;
		shouldRenderAfterIcon?: boolean;
		selection: string;
		setSelection: (newSort: SpaceViewsPersistenceOption) => void;
		isLicensed: boolean;
		canManageContentOrSuperAdmin: boolean;
	}
>;

const overlayCSS = css({
	width: '100%',
	height: '100%',
});
const overlayLoading = css({
	opacity: 0.5,
	pointerEvents: 'none',
	cursor: 'not-allowed',
});
const DisabledOverlay: FC<{ loading: boolean; children?: React.ReactNode }> = ({
	loading,
	children,
}) => (
	<div css={[overlayCSS, loading && overlayLoading]} data-vc="content-tree-search-overlay">
		{children}
	</div>
);

const SpaceViewsMenuLoader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViewsMenu" */ './SpaceViewsMenu.tsx'))
			.SpaceViewsMenu,
});

export const ContentTreeSearch: FC<ContentTreeSearchProps> = ({
	children,
	contentTreeSize,
	hideSearchRow,
	shouldRenderAfterIcon,
	selection,
	setSelection,
	isLicensed,
	canManageContentOrSuperAdmin,
	...hoverPageCardProps
}: ContentTreeSearchProps) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const intl = useIntl();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const inputRef = useRef<HTMLInputElement>(null);
	const { edition } = useSessionData();

	const [searchText, setSearchText] = useState('');
	const debouncedSearchText = useDebounce(searchText, 200);

	const { searchResult, loading, error } = useContentTreeSearch(
		debouncedSearchText,
		'sideNavigation',
		undefined,
		true,
	);

	const isCreationADropdown = useIsCreationDropdownEnabled();
	const { disableMultiSelect, exitMultiSelect } = useContentTreeSelectionActions();
	const [isMultiSelectActive] = useIsMultiSelectActive();

	const handleChange = useCallback(
		(e: any) => {
			const inputValue = e.currentTarget.value || '';
			if (inputValue.length > 0) {
				disableMultiSelect();
			} else {
				exitMultiSelect();
			}
			setSearchText(inputValue);
		},
		[disableMultiSelect, exitMultiSelect],
	);

	const handleOnClearSearch = useCallback(() => {
		setSearchText('');
		exitMultiSelect();
	}, [exitMultiSelect]);

	const onItemClick = useCallback(
		(id: string, index?: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'pageTree',
					source: 'pageTree',
					objectId: id,
					attributes: {
						spaceView: 'contentTreeSearch',
						navdexPointType: TOUCH,
						searchResultCount: searchResult?.length,
						searchResultIndex: index,
						contentTreeSize,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, searchResult, contentTreeSize],
	);

	const handleTextfieldFocus = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'focused',
				actionSubject: 'contentTreeSearch',
				source: 'sideNavigation',
				attributes: {
					navdexPointType: START_TOUCH,
					contentTreeSize,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentTreeSize]);

	return (
		<div data-vc="content-tree-search" {...ssrPlaceholderIdProp}>
			{!hideSearchRow && (
				<TextfieldWrapperDiv id="contentTreeSearch">
					<Flex columnGap="space.100">
						<Tooltip
							content={isMultiSelectActive ? intl.formatMessage(i18n.disabledSearchTooltip) : ''}
							position="bottom"
							component={ToolTipOverride}
						>
							{(tooltipProps) => (
								<Textfield
									{...tooltipProps}
									ref={mergeRefs([tooltipProps.ref, inputRef])}
									aria-label={intl.formatMessage(i18n.searchLabel)}
									aria-describedby="contentTreeSearchInfo"
									placeholder={intl.formatMessage(i18n.searchLabel)}
									onChange={handleChange}
									onFocus={handleTextfieldFocus}
									value={searchText}
									elemBeforeInput={
										<Box paddingInlineStart="space.100">
											<SearchIcon label="" color={token('color.icon.accent.gray')} />
										</Box>
									}
									elemAfterInput={
										searchText ? (
											<ClearSearchButton onClick={handleOnClearSearch}>
												<SelectClearIcon
													label={intl.formatMessage(i18n.clearSearch)}
													color={token('color.icon.subtle')}
												/>
											</ClearSearchButton>
										) : null
									}
									isCompact
									isDisabled={isMultiSelectActive}
								/>
							)}
						</Tooltip>
						<VisuallyHidden id="contentTreeSearchInfo">
							{intl.formatMessage(i18n.searchInputDescription)}
						</VisuallyHidden>

						{edition === 'PREMIUM' && isLicensed && canManageContentOrSuperAdmin && (
							<SpaceViewsMenuLoader
								selection={selection}
								setSelection={setSelection}
								isWhiteboardBetaEnabled={isCreationADropdown}
								edition={edition}
								canManageContentOrSuperAdmin={canManageContentOrSuperAdmin}
							/>
						)}
					</Flex>
				</TextfieldWrapperDiv>
			)}
			<DisabledOverlay loading={loading}>
				<div aria-live="assertive" aria-atomic="true">
					{error ? (
						<ErrorDisplay error={error}>
							<ContentTreeSearchErrorLoader />
						</ErrorDisplay>
					) : searchResult && searchResult.length === 0 ? (
						<ContentTreeSearchEmptyLoader />
					) : null}
				</div>
				{!error ? (
					!searchResult ? (
						children
					) : searchResult.length > 0 ? (
						<>
							<VisuallyHidden>
								<FormattedMessage
									{...i18n.searchResultsCount}
									values={{ count: searchResult.length }}
								/>
							</VisuallyHidden>
							<SpaceViewsList
								pagesList={searchResult}
								spaceKey={spaceKey}
								contentId={contentId}
								onItemClick={onItemClick}
								renderTitle={({ title }) => enrichExcerpt(title)}
								shouldRenderAfterIcon={shouldRenderAfterIcon}
								{...hoverPageCardProps}
							/>
						</>
					) : null
				) : null}
			</DisabledOverlay>
		</div>
	);
};
